import React, { useState } from 'react';
import StatCard from '../components/StatCard';
import useAPI from '../hooks/useAPI';
import { getRegions, getRegionDetail, validateRegion } from '../services/api';

export default function Regions() {
  const { data, loading } = useAPI(getRegions);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [detail, setDetail] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const regions = data?.regions || [];

  const handleSelect = async (regionId) => {
    setSelectedRegion(regionId);
    setLoadingDetail(true);
    try {
      const [d, v] = await Promise.all([
        getRegionDetail(regionId),
        validateRegion(regionId),
      ]);
      setDetail(d);
      setValidation(v);
    } catch (err) {
      setDetail(null);
      setValidation(null);
    }
    setLoadingDetail(false);
  };

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading regions...</div>;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Multi-Region Operations</h1>
      <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
        Data residency management across {regions.length} regions
      </p>

      <div style={styles.cardGrid}>
        <StatCard label="Total Regions" value={regions.length} color="#6c5ce7" />
        <StatCard
          label="Data Residency"
          value={regions.filter(r => r.data_residency).length}
          sub="regions with strict residency"
          color="#d63031"
        />
        <StatCard
          label="Robot Types"
          value={regions.reduce((s, r) => s + (r.robot_types?.length || 0), 0)}
          color="#0984e3"
        />
        <StatCard
          label="Logistics Centers"
          value={regions.reduce((s, r) => s + (r.logistics_centers || 0), 0)}
          color="#00b894"
        />
      </div>

      <div style={styles.twoCol}>
        {/* Region cards */}
        <div>
          <h2 style={styles.sectionTitle}>Regions</h2>
          {regions.map(region => (
            <div
              key={region.region}
              style={{
                ...styles.regionCard,
                borderLeftColor: selectedRegion === region.region ? '#6c5ce7' : 'transparent',
              }}
              onClick={() => handleSelect(region.region)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{region.display_name}</div>
                  <div style={{ fontSize: 12, color: '#636e72', marginTop: 4 }}>
                    {region.country} | {region.timezone} | {region.currency}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{region.panel_width}</span>
                  <div style={{ fontSize: 11, color: '#636e72' }}>panels</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {region.robot_types?.map(rt => (
                  <span key={rt} style={styles.tag}>{rt}</span>
                ))}
                {region.data_residency && (
                  <span style={{ ...styles.tag, background: '#ffe0e0', color: '#c0392b' }}>
                    Data Residency Required
                  </span>
                )}
                <span style={styles.tag}>{region.logistics_centers} centers</span>
              </div>

              <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
                {region.privacy_law}
              </div>
            </div>
          ))}
        </div>

        {/* Region detail */}
        <div>
          {!selectedRegion ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: 48, opacity: 0.2 }}>&#127758;</div>
              <div style={{ fontWeight: 600, marginTop: 8 }}>Select a Region</div>
              <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>
                Click a region to view details and deployment status
              </div>
            </div>
          ) : loadingDetail ? (
            <div style={{ padding: 40, color: '#999', textAlign: 'center' }}>Loading...</div>
          ) : detail ? (
            <>
              <h2 style={styles.sectionTitle}>{detail.display_name} — Configuration</h2>

              {/* Config details */}
              <div style={styles.detailCard}>
                <DetailRow label="Country" value={detail.country_code} />
                <DetailRow label="Timezone" value={detail.timezone} />
                <DetailRow label="Primary Language" value={detail.primary_language} />
                <DetailRow label="Other Languages" value={detail.secondary_languages?.join(', ')} />
                <DetailRow label="AWS Region" value={detail.aws_region} />
                <DetailRow label="S3 Bucket" value={detail.s3_bucket} />
                <DetailRow label="Currency" value={`${detail.currency} (${detail.currency_symbol})`} />
                <DetailRow label="Panel Width" value={`${detail.panel_width_m}m`} />
                <DetailRow label="Robot Types" value={detail.robot_types?.join(', ')} />
                <DetailRow
                  label="Data Residency"
                  value={detail.data_residency_required ? 'Required' : 'Not required'}
                  highlight={detail.data_residency_required}
                />
                <DetailRow label="Privacy Law" value={detail.privacy_law} />
              </div>

              {/* Safety thresholds */}
              <h2 style={{ ...styles.sectionTitle, marginTop: 20 }}>Safety Thresholds</h2>
              <div style={styles.detailCard}>
                <DetailRow label="Extreme Heat" value={`${detail.safety?.extreme_heat_c}°C`} />
                <DetailRow label="High Heat" value={`${detail.safety?.high_heat_c}°C`} />
                <DetailRow label="Wind Limit" value={`${detail.safety?.wind_limit_kmh} km/h`} />
                <DetailRow
                  label="Monsoon Season"
                  value={detail.safety?.monsoon_months?.length > 0
                    ? `Months: ${detail.safety.monsoon_months.join(', ')}`
                    : 'N/A'}
                />
              </div>

              {/* Deployment validation */}
              {validation && (
                <>
                  <h2 style={{ ...styles.sectionTitle, marginTop: 20 }}>Deployment Status</h2>
                  <div style={{
                    ...styles.detailCard,
                    borderLeft: `4px solid ${validation.checks?.deployment_ready ? '#00b894' : '#d63031'}`,
                  }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: validation.checks?.deployment_ready ? '#00b894' : '#d63031',
                      marginBottom: 8,
                    }}>
                      {validation.checks?.deployment_ready ? 'DEPLOYMENT READY' : 'NOT READY'}
                    </div>
                    <DetailRow label="MongoDB" value={validation.checks?.mongodb_uri_configured ? 'Configured' : 'Not set'} />
                    <DetailRow label="Logistics Centers" value={validation.checks?.logistics_centers_count} />
                    <DetailRow label="Languages" value={validation.checks?.languages_count} />
                    <DetailRow label="Robot Types" value={validation.checks?.robot_types?.join(', ')} />
                    {validation.checks?.missing_items?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#d63031' }}>Missing:</div>
                        {validation.checks.missing_items.map((item, i) => (
                          <div key={i} style={{ fontSize: 12, color: '#636e72', paddingLeft: 12 }}>
                            {item}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: '1px solid #f5f5f5',
    }}>
      <span style={{ fontSize: 13, color: '#636e72' }}>{label}</span>
      <span style={{
        fontSize: 13, fontWeight: 500,
        color: highlight ? '#d63031' : '#2d3436',
      }}>
        {value || '-'}
      </span>
    </div>
  );
}

const styles = {
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, margin: '20px 0' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  regionCard: {
    padding: 16, background: '#fff', borderRadius: 10, marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer',
    borderLeft: '4px solid transparent', transition: 'all 0.15s',
  },
  tag: {
    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: '#f0f0f0', color: '#636e72',
  },
  emptyState: {
    textAlign: 'center', padding: '60px 20px', background: '#fff',
    borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  detailCard: {
    padding: 16, background: '#fff', borderRadius: 10,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
};
